import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoaderCircle, CircleAlert } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';
import { supabase } from '@/lib/supabaseClient';
import { Telemetry } from '@/lib/telemetry';
import { createLogger } from '@/lib/debug';

const log = createLogger('auth.callback');

/**
 * Detect whether this callback is from a magic link or SSO.
 * Magic links arrive with ?token=...&type=magiclink (or type=email).
 * SSO callbacks arrive with a hash fragment #access_token=...
 */
function detectAuthMethod(location: ReturnType<typeof useLocation>): 'magic_link' | 'sso' {
  const params = new URLSearchParams(location.search);
  const type = params.get('type');
  if (type === 'magiclink' || type === 'email') return 'magic_link';
  // Also check hash fragment for token_type (SSO flow)
  if (location.hash.includes('access_token')) return 'sso';
  // Default: check if query has token (magic link verification redirect)
  if (params.has('token') || params.has('token_hash')) return 'magic_link';
  return 'sso';
}

export function OAuthCallbackPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function handleCallback() {
      const authMethod = detectAuthMethod(location);
      log.log('callback received', { authMethod, search: location.search, hash: location.hash ? '[present]' : '[none]' });

      try {
        // For magic links with query params (token + type), Supabase needs to
        // verify the token via verifyOtp or exchangeCodeForSession. The
        // supabase-js client auto-handles this when it detects the URL params
        // on initialization. We need to ensure the session is established first.
        if (authMethod === 'magic_link') {
          // Check for error params in query string (e.g., expired link)
          const params = new URLSearchParams(location.search);
          const errorParam = params.get('error');
          const errorDescription = params.get('error_description');
          if (errorParam) {
            log.warn('magic link error in URL params', { error: errorParam, description: errorDescription });
            setError(errorDescription || 'The sign-in link has expired. Please request a new one.');
            return;
          }

          // Check for hash fragment errors too
          const hashParams = new URLSearchParams(location.hash.replace('#', ''));
          const hashError = hashParams.get('error');
          const hashErrorDesc = hashParams.get('error_description');
          if (hashError) {
            log.warn('magic link error in hash', { error: hashError, description: hashErrorDesc });
            setError(hashErrorDesc || 'The sign-in link has expired. Please request a new one.');
            return;
          }

          // Wait briefly for supabase-js to detect and exchange the token
          log.log('waiting for supabase session exchange');
          const { data, error: sessionError } = await supabase.auth.getSession();
          log.log('session exchange result', {
            hasSession: !!data.session,
            error: sessionError?.message,
          });

          if (sessionError) {
            setError(sessionError.message);
            return;
          }
        }

        // Refresh auth to pick up the session (works for both SSO and magic link)
        log.log('calling refreshAuth');
        await refreshAuth();
        if (cancelled) return;

        // A reconnect-Graph flow stashes the route it was triggered from (e.g.
        // /fre mid-form) so we return the user there instead of the dashboard.
        // Returns null (→ '/') for a normal sign-in or any non-internal value.
        const dest = consumePostAuthRedirect() ?? '/';
        log.log('auth refreshed, navigating', { dest });
        Telemetry.userSignedIn({ auth_method: authMethod === 'magic_link' ? 'magic_link' : 'sso_entra_id' });
        navigate(dest, { replace: true });
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : 'Sign in failed';
        log.warn('callback error', msg);
        setError(msg);
        Telemetry.errorSwallowed({
          error_type: 'auth_callback_failure',
          operation: 'auth_callback',
          screen_name: '/auth/callback',
          error_message: msg,
        });
      }
    }

    handleCallback();

    return () => {
      cancelled = true;
    };
  }, [navigate, refreshAuth, location]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-sm text-center">
          <CircleAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-brand-charcoal mb-2">Sign In Failed</h2>
          <p className="text-sm text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/auth/login', { replace: true })}
            className="px-5 py-2 bg-brand-orange text-white text-sm font-semibold rounded-lg hover:bg-orange-600 transition-colors"
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center">
      <div className="w-full max-w-md bg-white rounded-2xl border border-slate-200 p-8 shadow-sm text-center">
        <LoaderCircle className="w-8 h-8 text-brand-orange animate-spin mx-auto mb-4" />
        <h2 className="text-lg font-bold text-brand-charcoal mb-1">Signing you in...</h2>
        <p className="text-sm text-slate-500">Completing authentication</p>
      </div>
    </div>
  );
}
